import { ProgramWeeks, ProgramWorkoutItemRecord } from '../../api/programs.interface';
import {
    ProgramImportCanonicalField,
    ProgramImportFieldMapping,
    ProgramImportIssue,
    ProgramImportParseResult,
} from './programs.types';

const HEADER_ALIASES: Record<ProgramImportCanonicalField, string[]> = {
    day: ['day', 'daynumber'],
    description: ['description', 'title', 'workout', 'workoutdescription', 'workoutname'],
    imageUrl: ['image', 'imagelink', 'imagepath', 'imageurl', 'imageuri', 'photourl', 'thumbnail'],
    level: ['level', 'programlevel'],
    primaryTag: ['primarytag', 'primarytags'],
    primaryType: ['primarytype'],
    programName: ['name', 'program', 'programname', 'programtitle'],
    rounds: ['round', 'rounds'],
    secondaryTag: ['secondarytag', 'secondarytags'],
    secondaryType: ['secondarytype'],
    time: ['duration', 'time'],
    week: ['week', 'weeknumber'],
};

const REQUIRED_IMPORT_FIELDS: ProgramImportCanonicalField[] = ['week', 'day', 'description', 'primaryType'];

const DELIMITER_VALUES = {
    csv: ',',
    tsv: '\t',
} as const;

type DelimiterType = keyof typeof DELIMITER_VALUES;

interface ParsedDelimitedContent {
    rows: string[][];
}

interface NormalizeRowResult {
    item?: ProgramWorkoutItemRecord;
    programNameCandidate?: string;
}

export function parseProgramImportFile(fileName: string, fileContent: string): ProgramImportParseResult {
    const normalizedContent = normalizeFileContent(fileContent);
    const detectedDelimiter = detectDelimiter(normalizedContent);
    const parsedContent = parseDelimitedContent(normalizedContent, DELIMITER_VALUES[detectedDelimiter]);
    const errors: ProgramImportIssue[] = [];
    const warnings: ProgramImportIssue[] = [];

    if (!parsedContent.rows.length) {
        errors.push(createImportIssue('error', 'The selected file is empty.'));

        return {
            canImport: false,
            detectedDelimiter,
            errors,
            fieldMappings: [],
            rowsImported: 0,
            suggestedProgramName: createSuggestedProgramNameFromFileName(fileName),
            warnings,
            weeks: {},
        };
    }

    const [headerRow, ...dataRows] = parsedContent.rows;
    const fieldMappings = createFieldMappings(headerRow);
    const headerIndexByField = createHeaderIndexByField(fieldMappings, warnings);

    const missingRequiredHeaders = REQUIRED_IMPORT_FIELDS.filter((field) => headerIndexByField[field] === undefined);

    if (missingRequiredHeaders.length) {
        errors.push(createImportIssue('error', `Missing required columns: ${missingRequiredHeaders.join(', ')}.`));
    }

    if (!dataRows.length) {
        errors.push(createImportIssue('error', 'The selected file does not contain any program rows.'));
    }

    const programNameCandidates = new Set<string>();
    const seenSlots = new Set<string>();
    const weeks: ProgramWeeks = {};

    dataRows.forEach((row, index) => {
        const rowNumber = index + 2;
        const normalizedRow = normalizeImportRow({
            fileName,
            headerIndexByField,
            row,
            rowNumber,
            errors,
            warnings,
        });

        if (!normalizedRow.item) {
            return;
        }

        if (normalizedRow.programNameCandidate) {
            programNameCandidates.add(normalizedRow.programNameCandidate);
        }

        const slotKey = `${normalizedRow.item.week}-${normalizedRow.item.day}`;

        if (seenSlots.has(slotKey)) {
            errors.push(createImportIssue('error', `Duplicate week/day slot detected for week ${normalizedRow.item.week} day ${normalizedRow.item.day}.`, rowNumber));
            return;
        }

        seenSlots.add(slotKey);

        if (!weeks[String(normalizedRow.item.week)]) {
            weeks[String(normalizedRow.item.week)] = {};
        }

        weeks[String(normalizedRow.item.week)][String(normalizedRow.item.day)] = normalizedRow.item;
    });

    if (programNameCandidates.size > 1) {
        errors.push(createImportIssue('error', 'This file contains rows from multiple program names/levels. Import one program at a time.'));
    }

    if (headerIndexByField.imageUrl === undefined) {
        warnings.push(createImportIssue('warning', 'No imageUrl column was detected. Imported days will need image URLs before save.'));
    }

    const suggestedProgramName = normalizeProgramNameCandidate(
        Array.from(programNameCandidates)[0] || createSuggestedProgramNameFromFileName(fileName),
    );

    return {
        canImport: errors.length === 0 && Object.keys(weeks).length > 0,
        detectedDelimiter,
        errors,
        fieldMappings,
        rowsImported: Object.values(weeks).reduce((total, weekSchedule) => total + Object.keys(weekSchedule).length, 0),
        suggestedProgramName,
        warnings,
        weeks: sortProgramWeeks(weeks),
    };
}

function normalizeImportRow({
    errors,
    fileName,
    headerIndexByField,
    row,
    rowNumber,
    warnings,
}: {
    errors: ProgramImportIssue[];
    fileName: string;
    headerIndexByField: Partial<Record<ProgramImportCanonicalField, number>>;
    row: string[];
    rowNumber: number;
    warnings: ProgramImportIssue[];
}): NormalizeRowResult {
    const week = readRequiredPositiveInteger(row, headerIndexByField.week, 'week', rowNumber, errors);
    const day = readRequiredPositiveInteger(row, headerIndexByField.day, 'day', rowNumber, errors);
    const description = readRequiredString(row, headerIndexByField.description, 'description', rowNumber, errors);
    const primaryType = readRequiredString(row, headerIndexByField.primaryType, 'primaryType', rowNumber, errors);

    if (week === undefined || day === undefined || description === undefined || primaryType === undefined) {
        return {};
    }

    if (day > 7) {
        errors.push(createImportIssue('error', 'Day must be between 1 and 7.', rowNumber));
        return {};
    }

    const explicitProgramName = readOptionalTrimmedString(row, headerIndexByField.programName);
    const rawLevel = readOptionalTrimmedString(row, headerIndexByField.level);
    const programNameCandidate = explicitProgramName || rawLevel || createSuggestedProgramNameFromFileName(fileName);
    const level = rawLevel || normalizeProgramNameCandidate(programNameCandidate);
    const imageUrl = readOptionalTrimmedString(row, headerIndexByField.imageUrl) || '';
    const rounds = readOptionalPositiveInteger(row, headerIndexByField.rounds, 'rounds', rowNumber, errors);
    const primaryTag = readOptionalTagString(row, headerIndexByField.primaryTag, 'primaryTag', rowNumber, errors);
    const secondaryTag = readOptionalTagString(row, headerIndexByField.secondaryTag, 'secondaryTag', rowNumber, errors);
    const secondaryType = readOptionalTrimmedString(row, headerIndexByField.secondaryType);
    const time = readOptionalTrimmedString(row, headerIndexByField.time);

    if (!imageUrl) {
        warnings.push(createImportIssue('warning', 'Missing imageUrl. This day must be updated before save.', rowNumber));
    }

    const item: ProgramWorkoutItemRecord = {
        day,
        description,
        imageUrl,
        level,
        primaryTag,
        primaryType,
        rounds,
        secondaryTag,
        secondaryType,
        time,
        week,
    };

    return {
        item,
        programNameCandidate,
    };
}

function createFieldMappings(headers: string[]): ProgramImportFieldMapping[] {
    return headers.map((sourceHeader) => {
        const normalizedHeader = normalizeHeaderToken(sourceHeader);

        return {
            canonicalField: getCanonicalField(normalizedHeader),
            normalizedHeader,
            sourceHeader,
        };
    });
}

function createHeaderIndexByField(
    fieldMappings: ProgramImportFieldMapping[],
    warnings: ProgramImportIssue[],
): Partial<Record<ProgramImportCanonicalField, number>> {
    return fieldMappings.reduce<Partial<Record<ProgramImportCanonicalField, number>>>((accumulator, mapping, index) => {
        if (!mapping.canonicalField) {
            warnings.push(createImportIssue('warning', `Ignoring unrecognized column "${mapping.sourceHeader}".`));
            return accumulator;
        }

        if (accumulator[mapping.canonicalField] !== undefined) {
            warnings.push(createImportIssue('warning', `Ignoring duplicate column "${mapping.sourceHeader}" for ${mapping.canonicalField}.`));
            return accumulator;
        }

        accumulator[mapping.canonicalField] = index;
        return accumulator;
    }, {});
}

function getCanonicalField(normalizedHeader: string): ProgramImportCanonicalField | undefined {
    return Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.includes(normalizedHeader))?.[0] as ProgramImportCanonicalField | undefined;
}

function createSuggestedProgramNameFromFileName(fileName: string): string {
    const strippedExtension = fileName.replace(/\.[^.]+$/, '');
    const normalizedName = strippedExtension.replace(/[_-]+/g, ' ').trim();

    return normalizeProgramNameCandidate(normalizedName || 'imported program');
}

function normalizeProgramNameCandidate(value: string): string {
    return value.trim().toLowerCase();
}

function normalizeHeaderToken(header: string): string {
    return header.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeFileContent(fileContent: string): string {
    return fileContent.replace(/^\uFEFF/, '').trim();
}

function detectDelimiter(fileContent: string): DelimiterType {
    const firstLine = fileContent.split(/\r?\n/).find((line) => line.trim());

    if (!firstLine) {
        return 'csv';
    }

    const tabCount = (firstLine.match(/\t/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;

    return tabCount > commaCount ? 'tsv' : 'csv';
}

function parseDelimitedContent(fileContent: string, delimiter: string): ParsedDelimitedContent {
    const rows: string[][] = [];
    let currentField = '';
    let currentRow: string[] = [];
    let inQuotes = false;

    for (let index = 0; index < fileContent.length; index += 1) {
        const character = fileContent[index];
        const nextCharacter = fileContent[index + 1];

        if (character === '"') {
            if (inQuotes && nextCharacter === '"') {
                currentField += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }

            continue;
        }

        if (character === delimiter && !inQuotes) {
            currentRow.push(currentField);
            currentField = '';
            continue;
        }

        if ((character === '\n' || character === '\r') && !inQuotes) {
            if (character === '\r' && nextCharacter === '\n') {
                index += 1;
            }

            currentRow.push(currentField);
            rows.push(currentRow);
            currentField = '';
            currentRow = [];
            continue;
        }

        currentField += character;
    }

    currentRow.push(currentField);
    rows.push(currentRow);

    return {
        rows: rows
            .map((row) => row.map((field) => field.trim()))
            .filter((row) => row.some((field) => field.trim())),
    };
}

function readRequiredPositiveInteger(
    row: string[],
    index: number | undefined,
    fieldName: string,
    rowNumber: number,
    errors: ProgramImportIssue[],
): number | undefined {
    const value = readOptionalTrimmedString(row, index);

    if (!value) {
        errors.push(createImportIssue('error', `Missing ${fieldName}.`, rowNumber));
        return undefined;
    }

    if (!/^\d+$/.test(value)) {
        errors.push(createImportIssue('error', `${fieldName} must be a positive whole number.`, rowNumber));
        return undefined;
    }

    return Number(value);
}

function readOptionalPositiveInteger(
    row: string[],
    index: number | undefined,
    fieldName: string,
    rowNumber: number,
    errors: ProgramImportIssue[],
): number | undefined {
    const value = readOptionalTrimmedString(row, index);

    if (!value) {
        return undefined;
    }

    if (!/^\d+$/.test(value)) {
        errors.push(createImportIssue('error', `${fieldName} must be a positive whole number.`, rowNumber));
        return undefined;
    }

    return Number(value);
}

function readRequiredString(
    row: string[],
    index: number | undefined,
    fieldName: string,
    rowNumber: number,
    errors: ProgramImportIssue[],
): string | undefined {
    const value = readOptionalTrimmedString(row, index);

    if (!value) {
        errors.push(createImportIssue('error', `Missing ${fieldName}.`, rowNumber));
        return undefined;
    }

    return value;
}

function readOptionalTagString(
    row: string[],
    index: number | undefined,
    fieldName: string,
    rowNumber: number,
    errors: ProgramImportIssue[],
): string | undefined {
    const value = readOptionalTrimmedString(row, index);

    if (!value) {
        return undefined;
    }

    const parts = value.split(',').map((part) => part.trim()).filter(Boolean);

    if (!parts.length) {
        return undefined;
    }

    if (parts.some((part) => !/^\d+$/.test(part))) {
        errors.push(createImportIssue('error', `${fieldName} must contain only comma-delimited tag numbers.`, rowNumber));
        return undefined;
    }

    return parts.join(', ');
}

function readOptionalTrimmedString(row: string[], index: number | undefined): string | undefined {
    if (index === undefined) {
        return undefined;
    }

    const value = row[index];

    if (value === undefined) {
        return undefined;
    }

    const trimmedValue = value.trim();

    return trimmedValue || undefined;
}

function sortProgramWeeks(weeks: ProgramWeeks): ProgramWeeks {
    return Object.keys(weeks)
        .sort((left, right) => Number(left) - Number(right))
        .reduce<ProgramWeeks>((accumulator, weekKey) => {
            accumulator[weekKey] = Object.keys(weeks[weekKey])
                .sort((left, right) => Number(left) - Number(right))
                .reduce<ProgramWeeks[string]>((weekAccumulator, dayKey) => {
                    weekAccumulator[dayKey] = weeks[weekKey][dayKey];
                    return weekAccumulator;
                }, {});

            return accumulator;
        }, {});
}

function createImportIssue(severity: 'error' | 'warning', message: string, rowNumber?: number): ProgramImportIssue {
    return {
        message,
        rowNumber,
        severity,
    };
}